import { useMemo, useState } from 'react'
import _ from 'lodash'
import { t, Trans } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import LoadingButton from '@mui/lab/LoadingButton'
import Autocomplete from '@mui/material/Autocomplete'
import Card from '@mui/material/Card'
import CardActionArea from '@mui/material/CardActionArea'
import CardContent from '@mui/material/CardContent'
import Chip from '@mui/material/Chip'
import MenuItem from '@mui/material/MenuItem'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'

import { WaitingRoom } from '@/core/models/waiting-room.model'
import { useGetConsultationReasonsQuery } from '@/core/services/consultation'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'
import { useCanReserveConsultation } from '@/pages/waiting-rooms/model/useCanReserveConsultation'
import { useReserveConsultation } from '@/pages/waiting-rooms/model/useReserveConsultation'
import { isWaitingRoomOpenAt } from '../model/openingHours'
import { SelectedPatient } from '../model/useSimplifiedReception'

type WaitingRoomStepProps = { patient: SelectedPatient; onPaid: (waitingRoomName: string, consultationId: string) => void }

type WaitingRoomCardProps = { room: WaitingRoom; selected: boolean; onSelect: (room: WaitingRoom) => void }

/** One waiting room; it can only be chosen when the usual reservation rules allow it (open, doctor available near closing). */
function WaitingRoomCard({ room, selected, onSelect }: WaitingRoomCardProps) {
  useLingui()
  const canReserve = useCanReserveConsultation(room)
  const open = isWaitingRoomOpenAt(room)
  const status = canReserve ? t`Open` : open ? t`No doctor available` : t`Closed`

  return (
    <Card variant="outlined" sx={{ borderWidth: 2, borderColor: selected ? 'primary.main' : undefined }}>
      <CardActionArea disabled={!canReserve} onClick={() => onSelect(room)} aria-label={room.name}>
        <CardContent>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">{room.name}</Typography>
            <Chip size="small" color={canReserve ? 'success' : 'default'} label={status} />
          </Stack>
          <Typography variant="body2" color="text.secondary">
            <Trans>{room.consultations?.length ?? 0} patient(s) waiting</Trans>
          </Typography>
          {!!room.specialities?.length && (
            <Typography variant="body2" color="text.secondary">{room.specialities.map(({ name }) => name).join(', ')}</Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

/**
 * Lists the waiting rooms the user can use. Once a room is chosen everything follows the usual
 * reservation: free room → live consultation, priced room → card payment.
 */
export function WaitingRoomStep({ patient, onPaid }: WaitingRoomStepProps) {
  useLingui()
  const { data, isLoading } = useListWaitingRoomsQuery({ query: {}, options: { limit: 0, skip: 0 } })
  const { data: consultationReasons } = useGetConsultationReasonsQuery({})
  const [selected, setSelected] = useState<WaitingRoom>()
  const [speciality, setSpeciality] = useState<string>()
  const [reason, setReason] = useState('')
  const { reserve, isReserving, paymentDialog } = useReserveConsultation(selected, {
    onPaid: (consultationId) => selected && onPaid(selected.name, consultationId),
  })
  const rooms = useMemo(() => data?.items ?? [], [data])
  const reasonOptions = useMemo(() => _.map(consultationReasons, 'name'), [consultationReasons])
  const specialities = selected?.specialities ?? []

  const select = (room: WaitingRoom) => {
    setSelected(room)
    setSpeciality(room.specialities?.length === 1 ? room.specialities[0].name : undefined)
  }

  return (
    <Stack spacing={2}>
      <Typography variant="h6"><Trans>Choose a waiting room for {patient.label}</Trans></Typography>
      {!isLoading && rooms.length === 0 && <Typography color="text.secondary"><Trans>No waiting room available</Trans></Typography>}
      {rooms.map((room) => (
        <WaitingRoomCard key={room._id} room={room} selected={selected?._id === room._id} onSelect={select} />
      ))}
      {specialities.length > 1 && (
        <TextField select label={t`Speciality`} value={speciality ?? ''} onChange={(event) => setSpeciality(event.target.value)}>
          {specialities.map(({ name }) => <MenuItem key={name} value={name}>{name}</MenuItem>)}
        </TextField>
      )}
      <Autocomplete
        freeSolo
        options={reasonOptions}
        inputValue={reason}
        onInputChange={(_event, value) => setReason(value)}
        renderInput={(params) => <TextField {...params} label={t`Reason for the consultation (optional)`} />}
      />
      <LoadingButton
        variant="contained"
        size="large"
        disabled={!selected || (specialities.length > 1 && !speciality)}
        loading={isReserving}
        onClick={() => reserve({ patient: patient.id, speciality, reason: reason.trim() || undefined })}
      >
        <Trans>Put in the waiting room</Trans>
      </LoadingButton>
      {paymentDialog}
    </Stack>
  )
}
